import { useMemo, useState } from 'react';
import { api } from '../../lib/api';
import type { MapProperty } from './types';
import { Dialog } from '../ui';

interface Props {
  compIds: string[];      // properties to save as comps
  pool: MapProperty[];    // candidate subject properties (loaded set)
  onClose: () => void;
}

export default function SaveCompsModal({ compIds, pool, onClose }: Props) {
  const [q, setQ] = useState('');
  const [subject, setSubject] = useState<MapProperty | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [] as MapProperty[];
    return pool
      .filter((p) => `${p.address || ''} ${p.city || ''}`.toLowerCase().includes(t))
      .slice(0, 25);
  }, [q, pool]);

  const link = async () => {
    if (!subject) return;
    setBusy(true);
    try {
      const res = await api.bulkLinkComps(subject.id, compIds);
      setResult(`Linked ${res.linked} comp${res.linked === 1 ? '' : 's'}${res.skipped ? ` (${res.skipped} already linked)` : ''} to ${subject.address || 'subject'}.`);
    } catch {
      setResult('Failed to link comps.');
    }
    setBusy(false);
  };

  return (
    /* OO5d: through the Dialog primitive. ⛔ It keeps z-1600 - this opens from the MAP, whose
       own panes and controls sit far above the page's default stacking. */
    <Dialog
      open
      onClose={onClose}
      title={`Save ${compIds.length} comp${compIds.length === 1 ? '' : 's'} to a subject property`}
      size="lg"
      zClassName="z-[1600]"
    >
        {result ? (
          <div className="p-6 text-center space-y-3">
            <p className="text-sm text-gray-800">{result}</p>
            <button onClick={onClose} className="px-4 py-1.5 text-sm rounded-lg bg-primary-500 text-white">Done</button>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <p className="text-xs text-gray-500">Pick the subject property these comps belong to.</p>
            <input
              autoFocus value={q} onChange={(e) => { setQ(e.target.value); setSubject(null); }}
              placeholder="Search your properties by address or city"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 border rounded-lg">
              {matches.length === 0 && <p className="p-3 text-xs text-gray-400">{q ? 'No match' : 'Type to search'}</p>}
              {matches.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSubject(p)}
                  className={`w-full text-left px-3 py-2 text-sm ${subject?.id === p.id ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                >
                  <span className="font-medium text-gray-900">{p.address || 'No address'}</span>
                  <span className="text-gray-500"> · {p.city}</span>
                </button>
              ))}
            </div>
            <button
              onClick={link}
              disabled={!subject || busy}
              className="w-full py-2 text-sm font-medium rounded-lg bg-primary-500 text-white disabled:opacity-50"
            >
              {busy ? 'Linking…' : subject ? `Link to ${subject.address}` : 'Select a subject property'}
            </button>
          </div>
        )}
    </Dialog>
  );
}
